import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Plus } from 'lucide-react';
import { StandardListLayout } from '@/shared/layout/StandardListLayout';
import { DataFilterToolbar, type FilterDefinition } from '@/components/ui/DataFilterToolbar';
import { ZoneStatsCards } from './components/ZoneStatsCards';
import { ZoneListTable } from './components/ZoneListTable';
import { ZoneDetailDrawer } from './components/ZoneDetailDrawer';
import { useZoneList } from './hooks/useZoneList';

export default function ZoneListPage() {
  const navigate = useNavigate();
  const [selectedZone, setSelectedZone] = useState<any | null>(null);
  const [activeTab, setActiveTab] = useState('all');
  
  const { 
    data: zones, 
    isLoading, 
    projects, 
    filters, 
    setFilter, 
    stats,
    refresh 
  } = useZoneList();
  
  const filteredZones = zones.filter((z: any) => {
    if (activeTab === 'all') return true;
    if (activeTab === 'thi-cong') return z.zone_type === 'construction';
    if (activeTab === 'bao-duong') return z.zone_type === 'maintenance' || z.zone_type === 'lawn' || z.zone_type === 'tree'; // Tùy thuộc vào data thực tế
    return true;
  });

  const filterDefs: FilterDefinition[] = [
    {
      id: 'tab',
      label: 'Danh mục',
      options: [
        { value: 'all', label: 'Tất cả' },
        { value: 'thi-cong', label: 'Thi công' },
        { value: 'bao-duong', label: 'Bảo dưỡng' }
      ]
    },
    {
      id: 'type',
      label: 'Loại khu vực',
      options: [
        { value: 'all', label: 'Tất cả khu vực' }
      ]
    },
    {
      id: 'projectId',
      label: 'Dự án',
      options: [
        { value: 'all', label: 'Tất cả dự án' },
        ...projects.map(p => ({ value: p.id, label: `[${p.code}] ${p.name}` }))
      ]
    },
    {
      id: 'status',
      label: 'Trạng thái',
      options: [
        { value: 'all', label: 'Tất cả trạng thái' },
        { value: 'active', label: 'Đang hoạt động' },
        { value: 'paused', label: 'Tạm dừng' },
        { value: 'completed', label: 'Đã hoàn thành' }
      ]
    }
  ];

  return (
    <StandardListLayout
      title="Khu vực"
      description="Quản lý và theo dõi các khu vực thi công và bảo dưỡng"
      isLoading={isLoading}
      viewMode="table"
      kpiSection={<ZoneStatsCards stats={stats} />}
      toolbarSection={
        <DataFilterToolbar
          search={filters.search || ''}
          onSearchChange={(v) => setFilter('search', v)}
          searchPlaceholder="Tìm kiếm theo tên, mã khu vực..."
          filterDefs={filterDefs}
          filterValues={{ 
            tab: activeTab, 
            type: 'all', 
            projectId: filters.projectId || 'all', 
            status: filters.status || 'all' 
          }}
          onFilterChange={(k, v) => {
            if (k === 'tab') {
              setActiveTab(v || 'all');
            } else {
              setFilter(k, v || 'all');
            }
          }}
          primaryAction={{
            label: "Tạo khu vực",
            icon: Plus,
            onClick: () => navigate('/zones/new')
          }}
          secondaryActions={[
            {
              label: "Xuất báo cáo",
              icon: Download,
              onClick: () => {}
            }
          ]}
        />
      }
      tableSection={
        <div className="bg-white rounded-md border border-slate-200 overflow-hidden flex flex-col h-full">
          <div className="flex-1 overflow-auto">
            <ZoneListTable 
              data={filteredZones} 
              onRowClick={(zone) => setSelectedZone(zone)}
              selectedId={selectedZone?.id}
            />
          </div>
        </div>
      }
      sidePanel={
        selectedZone ? (
          <ZoneDetailDrawer 
            isOpen={!!selectedZone} 
            onClose={() => setSelectedZone(null)} 
            data={selectedZone} 
          />
        ) : undefined
      }
    />
  );
}
